/**
 * Factory for a module-level "remember once per key" guard, shared by the
 * link-list caches (`resolved-url-cache`, `fired-event-cache`).
 *
 * Why module-level (not a ref/state): link-list subtrees unmount/remount on every
 * streamed chunk, which resets any component-scoped guard and re-runs "once"
 * behaviour repeatedly. Storing keys at module scope makes the guard survive
 * remounts. Callers create their own named instance so their keyspaces stay
 * isolated (e.g. URLs vs event keys).
 *
 * Bounded: capped at `maxEntries` with FIFO eviction, so it can't grow unbounded
 * over a long session. Evicting an old key is harmless (the guarded behaviour may
 * run once more if that key reappears).
 *
 * SSR-safe: the client check runs at call time (not at module init), so a module
 * imported during SSR still works after hydration. On the server the cache is
 * empty and `has` returns `false`, so no state leaks across requests.
 */

const isClient = (): boolean => typeof window !== 'undefined';

/** Default maximum number of keys to remember before FIFO eviction kicks in. */
export const DEFAULT_MAX_CACHE_ENTRIES = 500;

export interface StringSetCache {
	/**
	 * Clears the underlying set. Intended for tests only — flushing mid-session can
	 * reintroduce the very flicker / duplicate behaviour these caches prevent.
	 */
	__unsafeReset: () => void;
	/**
	 * Returns whether the given key has been recorded. Always returns `false` on
	 * the server, or when `key` is undefined.
	 */
	has: (key: string | undefined) => boolean;
	/**
	 * Records the given key. No-op on the server, when `key` is undefined, or when
	 * the key is already present.
	 */
	mark: (key: string | undefined) => void;
}

/**
 * Creates a new, independent {@link StringSetCache} instance.
 *
 * @param maxEntries Maximum number of keys to remember before FIFO eviction.
 */
export const createStringSetCache = (
	maxEntries: number = DEFAULT_MAX_CACHE_ENTRIES,
): StringSetCache => {
	const keys = new Set<string>();

	const has = (key: string | undefined): boolean =>
		isClient() && key !== undefined && keys.has(key);

	const mark = (key: string | undefined): void => {
		if (!isClient() || key === undefined || keys.has(key)) {
			return;
		}

		// FIFO eviction: drop the oldest entry once the cap is reached so the set
		// stays bounded over long sessions. `Set` iterates in insertion order, so
		// the first value is the oldest.
		if (keys.size >= maxEntries) {
			const oldest = keys.values().next().value;
			if (oldest !== undefined) {
				keys.delete(oldest);
			}
		}

		keys.add(key);
	};

	const __unsafeReset = (): void => {
		keys.clear();
	};

	return { has, mark, __unsafeReset };
};
